import { useToastStoreBase } from '@/stores/toasts';
import axios, { type Axios } from 'axios';
import * as Sentry from "@sentry/react";

export const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

interceptorSentryAxios(axiosInstance);

// Toast Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (!response.config.meta?.disableToasts) {
      const messages: string[] = [];
      recursiveSearch(
        response.data,
        "success_messages",
        (m) => Array.isArray(m) && messages.push(...m),
      );
      // NOTE: we only printing unique message (no doubles)
      toast("success")(Array.from(new Set(messages)));
    }
    return response;
  },
  (error) => {
    if (
      error?.response?.data &&
      !error?.response?.config?.meta?.disableToasts
    ) {
      const messages: string[] = [];
      recursiveSearch(
        error.response.data,
        "error_messages",
        (m) => Array.isArray(m) && messages.push(...m),
      );
      // NOTE: we only printing unique message (no doubles)
      toast("error")(Array.from(new Set(messages)));
    }
    return Promise.reject(error);
  }
);



function toast(variant: "success" | "error") {
  return (messages: unknown) => {
    if (typeof messages === "string") {
      useToastStoreBase.getState().add({
        variant,
        title: messages
      });
    } else if (Array.isArray(messages)) {
      messages
        .filter(m => typeof m === "string")
        .forEach(message => useToastStoreBase.getState().add({
          variant,
          title: message
        }));
    }
  };
}

function recursiveSearch(
  obj: object,
  target: string,
  callback: (value: unknown) => void
): void {
  for (const [key, value] of Object.entries(obj)) {
    if (key === target) {
      callback(value);
    }

    if (typeof value === 'object' && value !== null) {
      recursiveSearch(value, target, callback);
    }
  }
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    meta?: {
      disableToasts?: boolean;
    };
  }
}

function interceptorSentryAxios(axiosInstance: Axios) {
  axiosInstance.interceptors.request.use(
    (config) => {
      Sentry.addBreadcrumb({
        category: "axios",
        message: "Request",
        data: {
          url: config.url,
          method: config.method,
          headers: config.headers,
          body: config.data, // You can capture the request body here
          params: config.params,
        },
      });

      return config;
    },
    (error) => {
      const axiosError = {
        message: error.message,
        stack: error.stack, // The error stack trace
      };

      Sentry.addBreadcrumb({
        category: "axios",
        message: "Request Error",
        data: axiosError,
      });

      // Capture the detailed Axios error with additional data
      Sentry.withScope((scope) => {
        scope.setExtra("axiosError", axiosError);
        scope.captureException(error);
      });

      return Promise.reject(error);
    }
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response) => {
      Sentry.addBreadcrumb({
        category: "axios",
        message: "Response",
        data: {
          status: response.status,
          body: response.data, // You can capture the response body here
        },
      });

      return response;
    },
    (error) => {
      const data = error.response?.data?.data || error.response?.data;

      const axiosError = {
        message: error.message,
        stack: error.stack, // The error stack trace // You can capture the response body here
        status: error.response?.status,
      }

      Sentry.withScope((scope) => {
        scope.setExtra("axiosError", axiosError);
        scope.setExtra("axiosDataError", data);
        scope.captureException(error);
      });

      return Promise.reject(error);
    }
  );

  return axiosInstance;
}
